import React, { useState, useEffect } from "react";
import { X, Save, Trash2, Plus, ChevronDown, ChevronRight } from "lucide-react";
import {
  generateId,
  ITEM_CATEGORIES,
  WEAPON_KEYWORDS,
  ARMOUR_KEYWORDS,
  EQUIPMENT_KEYWORDS,
  formatCost,
  canAddItem,
} from "./helpers";
import { KeywordSelector } from "./KeywordSelector";
import { ItemTemplate, Warband, Model } from "./types";
import { getLimitDisplayInfo } from "./helpers";

import { Item } from "./types";

interface ItemEditorProps {
  item: ItemTemplate | null;
  onSave: (item: ItemTemplate | Item) => void;
  onCancel: () => void;
  isTemplateMode?: boolean;
  editingItem?: ItemTemplate | null;
  filterCategory?: string; // Filter items by category when selecting for models
  allItemTemplates?: ItemTemplate[];
  updateItemTemplates?: (templates: ItemTemplate[]) => void;
  warband?: Warband;
  models?: Model[];
  items?: Item[];
}

export const ItemEditor = ({
  item,
  onSave,
  onCancel,
  isTemplateMode = false,
  editingItem,
  filterCategory,
  allItemTemplates = [],
  updateItemTemplates,
  warband,
  models,
  items,
}: ItemEditorProps) => {
  const [itemTemplates, setItemTemplates] = useState<ItemTemplate[]>(allItemTemplates);
  const [view, setView] = useState(isTemplateMode ? "create" : "select");
  const [editingTemplate, setEditingTemplate] = useState<ItemTemplate | null>(
    editingItem || null
  );
  const [expandedSections, setExpandedSections] = useState({
    melee: true,
    ranged: true,
    armour: true,
    shield: true,
    grenade: true,
    equipment: true,
  });

  const [templateData, setTemplateData] = useState<ItemTemplate>(
    editingItem ||
      item || {
        id: generateId(),
        templateName: "",
        name: "",
        category: "melee",
        keywords: [],
        range: "",
        armourModifier: "",
        notes: "",
        cost: 0,
        costCurrency: "free",
        limit: undefined,
      }
  );

  // Handle initial view setup based on editing state
  useEffect(() => {
    if (isTemplateMode && editingItem) {
      setView("edit-template");
    } else if (isTemplateMode) {
      setView("create");
    } else {
      setView("select");
    }
  }, [isTemplateMode, editingItem]);


  const resetTemplateData = () => {
    setTemplateData({
      id: generateId(),
      templateName: "",
      name: "",
      category: "melee",
      keywords: [],
      range: "",
      armourModifier: "",
      notes: "",
      cost: 0,
      costCurrency: "free",
      limit: undefined,
    });
  };

  const handleSaveTemplate = () => {
    if (!templateData.templateName.trim()) {
      alert("Please enter a template name");
      return;
    }
    if (!templateData.name) {
      alert("Please enter item name");
      return;
    }

    const template = {
      ...templateData,
      id: editingTemplate ? editingTemplate.id : generateId(),
      templateName: templateData.templateName.trim(),
    };

    let updatedTemplates;
    if (editingTemplate) {
      updatedTemplates = itemTemplates.map((t) =>
        t.id === editingTemplate.id ? template : t
      );
    } else {
      updatedTemplates = [...itemTemplates, template];
    }

    if (updateItemTemplates) {
      updateItemTemplates(updatedTemplates);
    }
    setView("select");
    setEditingTemplate(null);
    resetTemplateData();
    alert(
      `Template "${template.templateName}" ${
        editingTemplate ? "updated" : "created"
      }!`
    );
  };

  const handleDeleteTemplate = (templateId: string) => {
    if (!confirm("Delete this template?")) return;
    const updatedTemplates = itemTemplates.filter((t) => t.id !== templateId);
    if (updateItemTemplates) {
      updateItemTemplates(updatedTemplates);
    }
  };

  const handleEditTemplate = (template: ItemTemplate) => {
    setEditingTemplate(template);
    setTemplateData({ ...template });
    setView("edit-template");
  };

  const handleAddFromTemplate = (template: ItemTemplate) => {
    // Check item limits if we have the required data
    if (warband && items && models && allItemTemplates) {
      const validation = canAddItem(template, warband, items, models, allItemTemplates);
      if (!validation.allowed) {
        alert(validation.reason);
        return;
      }
    }

    const newItem = {
      id: generateId(),
      name: template.name,
      category: template.category,
      keywords: [...template.keywords],
      range: template.range,
      armourModifier: template.armourModifier,
      notes: template.notes,
      cost: template.cost,
      costCurrency: template.costCurrency,
    };

    onSave(newItem);
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const getKeywordOptions = (category: string) => {
    if (category === "melee" || category === "ranged" || category === "grenade")
      return WEAPON_KEYWORDS;
    if (category === "armour" || category === "shield") return ARMOUR_KEYWORDS;
    if (category === "equipment") return EQUIPMENT_KEYWORDS;
    return [];
  };

  const showArmourModifier =
    (view === "edit-instance" &&
      (templateData.category === "armour" ||
        templateData.category === "shield")) ||
    ((view === "create" || view === "edit-template") &&
      (templateData.category === "armour" ||
        templateData.category === "shield"));

  const categorizeTemplates = () => {
    const categories: Record<string, ItemTemplate[]> = {
      melee: [],
      ranged: [],
      armour: [],
      shield: [],
      grenade: [],
      equipment: [],
    };
    itemTemplates.forEach((template) => {
      if (categories[template.category]) {
        categories[template.category].push(template);
      }
    });
    return categories;
  };

  const categorizedTemplates = categorizeTemplates();

  if (isTemplateMode || view === "create" || view === "edit-template") {
    return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" style={{backgroundImage: 'url(/images/zhi-3-2.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'overlay'}}>
      <div className="bg-gray-950/01 rounded border border-gray-700 shadow-lg p-4 max-w-4xl w-full max-h-[90vh] overflow-y-auto backdrop-blur-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-white">
              {view === "create"
                ? "Create New Item Template"
                : "Edit Item Template"}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-white"
            >
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Template Name
              </label>
              <input
                type="text"
                value={templateData.templateName}
                onChange={(e) =>
                  setTemplateData({
                    ...templateData,
                    templateName: e.target.value,
                  })
                }
                placeholder="e.g., 'Greatsword +1'"
                className="w-full bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Item Name
              </label>
              <input
                type="text"
                value={templateData.name}
                onChange={(e) =>
                  setTemplateData({ ...templateData, name: e.target.value })
                }
                className="w-full bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Category
              </label>
              <select
                value={templateData.category}
                onChange={(e) =>
                  setTemplateData({
                    ...templateData,
                    category: e.target.value,
                  })
                }
                className="w-full bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none"
              >
                {ITEM_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Cost
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={templateData.cost}
                  onChange={(e) =>
                    setTemplateData({
                      ...templateData,
                      cost: parseInt(e.target.value) || 0,
                    })
                  }
                  disabled={templateData.costCurrency === "free"}
                  className="flex-1 bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none disabled:opacity-50"
                />
                <select
                  value={templateData.costCurrency}
                  onChange={(e) =>
                    setTemplateData({
                      ...templateData,
                      costCurrency: e.target.value as "free" | "ducats" | "glory",
                    })
                  }
                  className="bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none"
                >
                  <option value="free">Free</option>
                  <option value="ducats">Ducats 👑</option>
                  <option value="glory">Glory ☼</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Limit (optional)
                </label>
                <input
                  type="number"
                  value={templateData.limit || ""}
                  onChange={(e) =>
                    setTemplateData({
                      ...templateData,
                      limit: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  placeholder="Max number of items..."
                  className="w-full bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none"
                />
              </div>
            </div>

            {(templateData.category === "ranged" ||
              templateData.category === "grenade") && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Range
                </label>
                <input
                  type="text"
                  value={templateData.range}
                  onChange={(e) =>
                    setTemplateData({ ...templateData, range: e.target.value })
                  }
                  className="w-full bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none"
                />
              </div>
            )}

            {showArmourModifier && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Armour Modifier
                </label>
                <input
                  type="text"
                  value={templateData.armourModifier}
                  onChange={(e) =>
                    setTemplateData({
                      ...templateData,
                      armourModifier: e.target.value,
                    })
                  }
                  className="w-full bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Keywords
              </label>
              <KeywordSelector
                selected={templateData.keywords}
                onChange={(kw) =>
                  setTemplateData({ ...templateData, keywords: kw })
                }
                availableKeywords={getKeywordOptions(templateData.category)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                value={templateData.notes}
                onChange={(e) =>
                  setTemplateData({ ...templateData, notes: e.target.value })
                }
                className="w-full bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none"
                rows={4}
              />
            </div>
          </div>

          <div className="flex gap-2 mt-6">
            <button
              onClick={handleSaveTemplate}
              className="flex-1 bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800"
            >
              <Save size={16} className="inline mr-2" />
              {editingTemplate ? "Update Template" : "Save Template"}
            </button>
            <button
              onClick={() => {
                setView("select");
                setEditingTemplate(null);
                resetTemplateData();
              }}
              className="flex-1 bg-red-900 hover:bg-red-800 text-white px-4 py-2 rounded border border-red-800 font-semibold uppercase tracking-wider"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" style={{backgroundImage: 'url(/images/zhi-3-2.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'overlay'}}>
      <div className="bg-gray-950/01 rounded border border-gray-700 shadow-lg p-4 max-w-4xl w-full max-h-[90vh] overflow-y-auto backdrop-blur-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-white">
            Add Item from Template
          </h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="mb-4">
          <button
            onClick={() => {
              resetTemplateData();
              setView("create");
            }}
            className="w-full bg-red-900 text-white px-4 py-3 rounded hover:bg-red-800 flex items-center justify-center gap-2 font-semibold"
          >
            <Plus size={20} /> Create New Item Template
          </button>
        </div>

        {itemTemplates.length === 0 ? (
          <div className="bg-gray-700 rounded p-8 text-center">
            <p className="text-gray-300 mb-4">No item templates created yet.</p>
            <p className="text-gray-400 text-sm">
              Create templates to quickly add items to your models!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {(filterCategory ? [filterCategory] : ITEM_CATEGORIES).map((category) => {
              const templates = categorizedTemplates[category];
              if (templates.length === 0) return null;

              return (
                <div
                  key={category}
                  className="bg-gray-900/90 rounded border border-gray-700 shadow-lg overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-gray-700">
                    <h3 className="text-lg font-bold text-gray-300 uppercase tracking-wider">
                      {filterCategory ? `SELECT ${category.toUpperCase()} ITEM` : `${category.toUpperCase()} (${templates.length})`}
                    </h3>
                  </div>
                  <div className="p-4 space-y-2">
                      {templates.map((template) => (
                        <ItemTemplateCard
                          key={template.id}
                          template={template}
                          onAdd={handleAddFromTemplate}
                          onEdit={handleEditTemplate}
                          onDelete={handleDeleteTemplate}
                          warband={warband}
                          models={models}
                          items={items}
                          allItemTemplates={itemTemplates}
                        />
                      ))}
                    </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ItemTemplateCard
const ItemTemplateCard = ({ template, onAdd, onEdit, onDelete, warband, models, items, allItemTemplates }: {
  template: ItemTemplate;
  onAdd: (template: ItemTemplate) => void;
  onEdit: (template: ItemTemplate) => void;
  onDelete: (id: string) => void;
  warband?: Warband;
  models?: Model[];
  items?: Item[];
  allItemTemplates?: ItemTemplate[];
}) => {
  const limitInfo = warband && models && items ? getLimitDisplayInfo(template, warband, items, models, allItemTemplates) : null;
  return (
    <div className="bg-gray-900/90 rounded p-3 hover:bg-gray-800/90 transition border border-gray-700">
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <h4 className="text-lg font-bold text-white">
            {template.templateName}
          </h4>
          <div className="text-gray-300 text-sm">{template.name}</div>
          <div className="flex gap-3 mt-1 text-xs text-gray-400">
            {template.range && <span>Range: {template.range}"</span>}
            {template.armourModifier && <span>{template.armourModifier}</span>}
            <span className="text-yellow-400 font-semibold">
              {formatCost(template.cost, template.costCurrency)}
            </span>
            {limitInfo?.limit && (
              <span className={`font-semibold ${limitInfo.isAtLimit ? 'text-red-400' : 'text-gray-400'}`}>
                {limitInfo.current}/{limitInfo.limit}
              </span>
            )}
          </div>
          {template.keywords.length > 0 && (
            <div className="mt-1 text-xs text-gray-400">
              {template.keywords.join(", ")}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onAdd(template)}
          disabled={limitInfo?.isAtLimit}
          className={`flex-1 px-3 py-1 rounded text-sm font-semibold ${
            limitInfo?.isAtLimit
              ? 'bg-gray-700 text-gray-500 cursor-not-allowed border border-gray-600'
              : 'bg-red-900 text-white hover:bg-red-800'
          }`}
        >
          {limitInfo?.isAtLimit ? 'At Limit' : 'Add'}
        </button>
        <button
          onClick={() => onEdit(template)}
          className="bg-red-900/50 text-red-300 px-3 py-1 rounded hover:bg-red-900/70 text-sm border border-red-800"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(template.id)}
          className="bg-red-900 text-white px-3 py-1 rounded hover:bg-red-800"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};
